import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DurationColumnFormat } from '@blaze-shared/api-contracts-domain';
import {
  durationInputTransform,
  durationPaste,
  formatDuration,
  formatDurationForSaving,
} from '@blaze-shared/shared-feature';

import { BaseCellInputComponent } from '../base-cell-input.component';

@UntilDestroy()
@Component({
  selector: 'blaze-cell-input-duration',
  template: `<input
    #durationInput
    type="text"
    [formControl]="durationDisplayValue"
    [class.readonly]="column.isComputed"
    [attr.readonly]="column.isComputed ? true : null"
    (beforeinput)="durationInputTransformFn($event, durationDisplayValue)"
    (paste)="durationInputPasteFn($event, durationDisplayValue)"
    class="input-string"
  />`,
  styleUrls: ['../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
})
export class CellInputDurationComponent extends BaseCellInputComponent<string> implements OnInit {
  @ViewChild('durationInput') durationInputRef!: ElementRef<HTMLInputElement>;

  durationDisplayValue: FormControl = new FormControl();

  durationInputTransformFn = (event: InputEvent, formControl: UntypedFormControl): void => {
    durationInputTransform(event, formControl, this.durationFormat);
  };
  durationInputPasteFn = (event: ClipboardEvent, formControl: UntypedFormControl): void => {
    durationPaste(event, formControl, this.durationFormat);
  };

  override get nativeElement(): HTMLInputElement | null {
    return this.durationInputRef?.nativeElement || null;
  }

  get durationFormat(): DurationColumnFormat {
    return this.column.format?.durationFormat || 'h:mm';
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.durationDisplayValue = this.createDurationFormControl();
  }

  private getValueForDurationInput(value: typeof this.value): string {
    if (!(typeof value === 'string' || value == null)) {
      return '';
    }
    return (value && formatDuration(value, this.durationFormat)) || '';
  }

  private getValueToEmit(value: string | null): string {
    return (value && formatDurationForSaving(value, this.durationFormat)) || '';
  }

  private createDurationFormControl(): FormControl {
    const formControl = new FormControl({
      value: this.getValueForDurationInput(this.value),
      disabled: this.isDisabled,
    });

    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const newValue = this.getValueToEmit(value);
      this.formControl.setValue(newValue, { emitEvent: true });
    });

    return formControl;
  }
}
